/* ============================================================================
   ROWMAN — core theme script
   Provides the module registry every other ROWMAN script uses so behaviour
   initialises correctly on first paint AND on every Theme Editor event
   (section load / unload / select, block select / deselect) without
   double-binding listeners or leaking requestAnimationFrame loops.
   ============================================================================ */
(function () {
  'use strict';

  /* ───────────────────────── module registry ───────────────────────── */

  var modules = [];
  var teardowns = [];

  /**
   * Register a module.
   * @param {Object} def
   *   name   {string}   unique id
   *   scope  {string}   CSS selector for the root element(s) it binds to
   *   mount  {Function} (rootEl) => optional teardown function
   */
  function register(def) {
    modules.push(def);
    if (document.readyState !== 'loading') mount(def, document);
  }

  function mount(def, container) {
    var roots = def.scope
      ? Array.prototype.slice.call(container.querySelectorAll(def.scope))
      : [document.documentElement];

    // container itself may BE the root (section reload passes the section el)
    if (def.scope && container.matches && container.matches(def.scope)) {
      roots.push(container);
    }

    roots.forEach(function (root) {
      var flag = 'rwInit' + def.name;
      if (root.dataset && root.dataset[flag] === '1') return;   // already bound
      if (root.dataset) root.dataset[flag] = '1';
      try {
        var down = def.mount(root);
        if (typeof down === 'function') {
          teardowns.push({ name: def.name, root: root, fn: down });
        }
      } catch (err) {
        console.error('[ROWMAN] module "' + def.name + '" failed to mount', err);
      }
    });
  }

  function mountAll(container) {
    modules.forEach(function (def) { mount(def, container || document); });
  }

  function unmount(container) {
    teardowns = teardowns.filter(function (t) {
      if (container === document || container.contains(t.root) || container === t.root) {
        try { t.fn(); } catch (err) { console.error('[ROWMAN] teardown failed', err); }
        if (t.root.dataset) delete t.root.dataset['rwInit' + t.name];
        return false;
      }
      return true;
    });
  }

  /* ───────────────────────── motion preference ───────────────────────── */

  var reduceQuery = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false, addEventListener: function () {} };

  function motionMode() {
    var setting = document.documentElement.getAttribute('data-rowman-motion-setting') || 'full';
    if (reduceQuery.matches) return 'off';
    return setting;
  }

  function applyMotion() {
    document.documentElement.setAttribute('data-rowman-motion', motionMode());
  }

  function motionAllowed() {
    return motionMode() === 'full';
  }

  if (reduceQuery.addEventListener) {
    reduceQuery.addEventListener('change', applyMotion);
  } else if (reduceQuery.addListener) {
    reduceQuery.addListener(applyMotion);
  }
  applyMotion();

  /* ───────────────────────── shared helpers ───────────────────────── */

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
                  'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trapFocus(panel) {
    function onKey(e) {
      if (e.key !== 'Tab') return;
      var items = Array.prototype.filter.call(
        panel.querySelectorAll(FOCUSABLE),
        function (el) { return el.offsetParent !== null || el === document.activeElement; }
      );
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    panel.addEventListener('keydown', onKey);
    return function () { panel.removeEventListener('keydown', onKey); };
  }

  var toastTimer = null;
  function toast(message) {
    var el = document.getElementById('rowmanToast');
    if (!el) return;
    el.textContent = message;
    el.classList.add('show');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { el.classList.remove('show'); }, 2600);
  }

  function announce(message) {
    var live = document.getElementById('rowmanLiveRegion');
    if (!live) return;
    live.textContent = '';
    setTimeout(function () { live.textContent = message; }, 60);
  }

  function money(cents) {
    var format = window.ROWMAN && window.ROWMAN.moneyFormat;
    var value = (cents / 100).toFixed(2);
    if (!format) return '$' + value;
    // Shopify money_format uses {{amount}} style placeholders; substitute the
    // common ones so cart JS output matches Liquid-rendered prices.
    return format
      .replace(/\{\{\s*amount\s*\}\}/g, value)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(cents / 100)))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, value.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g,
               String(Math.round(cents / 100)));
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function safeLocal(key, fallback) {
    try {
      var v = window.localStorage.getItem(key);
      return v === null ? fallback : JSON.parse(v);
    } catch (e) { return fallback; }
  }
  function setLocal(key, value) {
    try { window.localStorage.setItem(key, JSON.stringify(value)); } catch (e) {}
  }

  /* Public namespace ------------------------------------------------------ */
  window.ROWMAN = window.ROWMAN || {};
  window.ROWMAN.register = register;
  window.ROWMAN.mountAll = mountAll;
  window.ROWMAN.trapFocus = trapFocus;
  window.ROWMAN.toast = toast;
  window.ROWMAN.announce = announce;
  window.ROWMAN.money = money;
  window.ROWMAN.debounce = debounce;
  window.ROWMAN.motionAllowed = motionAllowed;
  window.ROWMAN.motionMode = motionMode;
  window.ROWMAN.local = { get: safeLocal, set: setLocal };
  window.ROWMAN.FOCUSABLE = FOCUSABLE;

  /* ───────────────────────── boot + Shopify events ───────────────────────── */

  function boot() { mountAll(document); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    mountAll(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    unmount(e.target);
  });
  document.addEventListener('shopify:section:select', function (e) {
    mountAll(e.target);
  });
  document.addEventListener('shopify:block:select', function (e) {
    mountAll(e.target.closest('[data-section-id]') || document);
    if (e.target.scrollIntoView) {
      e.target.scrollIntoView({ block: 'center', behavior: motionAllowed() ? 'smooth' : 'auto' });
    }
  });

  /* ═══════════════════════ MODULE: scroll reveals ═══════════════════════
     Replaces the prototype's SPA router animation re-trigger. Elements fade
     up once when they enter the viewport. */

  register({
  name: 'Reveal',
  scope: null,
  mount: function () {
    var selector = '.rw-reveal:not(.is-visible), .line-reveal:not(.in-view)';

    function reveal(el) {
      el.classList.add('is-visible');
      el.classList.add('in-view');
    }

    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll(selector).forEach(reveal);
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        reveal(entry.target);
        io.unobserve(entry.target);
      });
    }, {
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.05
    });

    function arm(root) {
      var group = 0;

      (root || document).querySelectorAll(selector).forEach(function (el) {
        if (
          el.classList.contains('rw-reveal') &&
          !el.style.getPropertyValue('--rw-delay')
        ) {
          el.style.setProperty(
            '--rw-delay',
            Math.min(group * 0.07, 0.6) + 's'
          );
          group++;
        }

        io.observe(el);
      });
    }

    arm(document);
    window.ROWMAN.armReveals = arm;

    document.addEventListener('shopify:section:load', function (event) {
      arm(event.target);
    });

    return function () {
      io.disconnect();
    };
  }
});
/* ==================== MODULE: mobile footer accordion ==================== */

register({
  name: 'FooterAccordion',
  scope: 'footer',

  mount: function (footer) {
    var menus = Array.prototype.slice.call(
      footer.querySelectorAll('[data-footer-menu]')
    );

    var mobileView = window.matchMedia('(max-width: 760px)');

    function closeMenu(menu) {
      var button = menu.querySelector('[data-footer-toggle]');

      menu.classList.remove('is-open');

      if (button) {
        button.setAttribute('aria-expanded', 'false');
      }
    }

    function handleClick(event) {
      if (!mobileView.matches) return;

      var button = event.currentTarget;
      var selectedMenu = button.closest('[data-footer-menu]');
      var willOpen = !selectedMenu.classList.contains('is-open');

      menus.forEach(function (menu) {
        closeMenu(menu);
      });

      if (willOpen) {
        selectedMenu.classList.add('is-open');
        button.setAttribute('aria-expanded', 'true');
      }
    }

    function handleBreakpointChange() {
      if (!mobileView.matches) {
        menus.forEach(function (menu) {
          closeMenu(menu);
        });
      }
    }

    menus.forEach(function (menu) {
      var button = menu.querySelector('[data-footer-toggle]');

      if (button) {
        button.addEventListener('click', handleClick);
      }
    });

    if (mobileView.addEventListener) {
      mobileView.addEventListener('change', handleBreakpointChange);
    } else {
      mobileView.addListener(handleBreakpointChange);
    }

    return function () {
      menus.forEach(function (menu) {
        var button = menu.querySelector('[data-footer-toggle]');

        if (button) {
          button.removeEventListener('click', handleClick);
        }
      });

      if (mobileView.removeEventListener) {
        mobileView.removeEventListener('change', handleBreakpointChange);
      } else {
        mobileView.removeListener(handleBreakpointChange);
      }
    };
  }
});
  /* ═══════════════════════ MODULE: header ═══════════════════════ */

  register({
    name: 'Header',
    scope: '[data-rowman-header]',
    mount: function (root) {
      var lastY = window.scrollY;
      function onScroll() {
        var y = window.scrollY;
        root.classList.toggle('is-scrolled', y > 24);
        lastY = y;
      }
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
      return function () { window.removeEventListener('scroll', onScroll); };
    }
  });

  /* ═══════════════════════ MODULE: mobile navigation drawer ═══════════════════════
     Approved in Phase 1 §9.3. Reuses the cart drawer's glass, hairlines,
     typography and transition. Keyboard accessible, focus trapped,
     Escape closes, overlay closes. */

  register({
    name: 'NavDrawer',
    scope: '[data-nav-drawer]',
    mount: function (drawer) {
      var overlay = document.querySelector('[data-nav-overlay]');
      var toggles = document.querySelectorAll('[data-nav-toggle]');
      var closeBtn = drawer.querySelector('[data-nav-close]');
      var lastFocus = null;
      var releaseTrap = null;

      function open() {
        lastFocus = document.activeElement;
        drawer.classList.add('is-open');
        drawer.setAttribute('aria-hidden', 'false');
        if (overlay) {
          overlay.classList.add('is-open');
          overlay.setAttribute('aria-hidden', 'false');
        }
        document.body.classList.add('nav-open');
        toggles.forEach(function (t) { t.setAttribute('aria-expanded', 'true'); });
        releaseTrap = trapFocus(drawer);
        var first = drawer.querySelector(FOCUSABLE);
        if (first) first.focus();
      }

      function close() {
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
        if (overlay) {
          overlay.classList.remove('is-open');
          overlay.setAttribute('aria-hidden', 'true');
        }
        document.body.classList.remove('nav-open');
        toggles.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
        if (releaseTrap) { releaseTrap(); releaseTrap = null; }
        if (lastFocus && lastFocus.focus) lastFocus.focus();
      }

      function onToggle(e) {
        e.preventDefault();
        drawer.classList.contains('is-open') ? close() : open();
      }
      function onKey(e) {
        if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
      }

      toggles.forEach(function (t) { t.addEventListener('click', onToggle); });
      if (closeBtn) closeBtn.addEventListener('click', close);
      if (overlay) overlay.addEventListener('click', close);
      document.addEventListener('keydown', onKey);
      drawer.querySelectorAll('a[href]').forEach(function (a) {
        a.addEventListener('click', function () { setTimeout(close, 120); });
      });

      return function () {
        toggles.forEach(function (t) { t.removeEventListener('click', onToggle); });
        if (closeBtn) closeBtn.removeEventListener('click', close);
        if (overlay) overlay.removeEventListener('click', close);
        document.removeEventListener('keydown', onKey);
        document.body.classList.remove('nav-open');
      };
    }
  });

  /* ═══════════════════════ MODULE: search overlay + predictive ═══════════════════════
     Phase 1 §9.2 option A. Uses Shopify's native predictive search endpoint —
     no third-party dependency. Falls back to the search results page. */

  register({
    name: 'SearchOverlay',
    scope: '[data-search-overlay]',
    mount: function (overlay) {
      var toggles = document.querySelectorAll('[data-search-toggle]');
      var closeBtn = overlay.querySelector('[data-search-close]');
      var input = overlay.querySelector('[data-search-input]');
      var results = overlay.querySelector('[data-search-results]');
      var form = overlay.querySelector('form');
      var predictiveEnabled = overlay.getAttribute('data-predictive') === 'true';
      var lastFocus = null;
      var releaseTrap = null;
      var controller = null;

      function open(e) {
        if (e) e.preventDefault();
        lastFocus = document.activeElement;
        overlay.classList.add('is-open');
        overlay.setAttribute('aria-hidden', 'false');
        document.body.classList.add('search-open');
        toggles.forEach(function (t) { t.setAttribute('aria-expanded', 'true'); });
        releaseTrap = trapFocus(overlay);
        if (input) setTimeout(function () { input.focus(); }, 60);
      }

      function close() {
        overlay.classList.remove('is-open');
        overlay.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('search-open');
        toggles.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
        if (releaseTrap) { releaseTrap(); releaseTrap = null; }
        if (lastFocus && lastFocus.focus) lastFocus.focus();
      }

      function onKey(e) {
        if (e.key === 'Escape' && overlay.classList.contains('is-open')) close();
      }

      var run = debounce(function () {
        if (!predictiveEnabled || !input || !results) return;
        var q = input.value.trim();
        if (q.length < 2) { results.innerHTML = ''; return; }

        results.innerHTML =
          '<div class="search-loading"><span class="dot"></span>' +
          (window.ROWMAN.strings.searching || 'Searching') + '</div>';

        if (controller && controller.abort) controller.abort();
        controller = ('AbortController' in window) ? new AbortController() : null;

        var url = (window.ROWMAN.routes.predictiveSearchUrl || '/search/suggest') +
                  '?q=' + encodeURIComponent(q) +
                  '&resources[type]=product,article,collection,page' +
                  '&resources[limit]=8' +
                  '&resources[options][unavailable_products]=last' +
                  '&section_id=rowman-predictive-search';

        fetch(url, controller ? { signal: controller.signal } : undefined)
          .then(function (r) {
            if (!r.ok) throw new Error('predictive search ' + r.status);
            return r.text();
          })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var payload = doc.querySelector('[data-predictive-payload]');
            results.innerHTML = payload ? payload.innerHTML : '';
            if (window.ROWMAN.armReveals) window.ROWMAN.armReveals(results);
          })
          .catch(function (err) {
            if (err && err.name === 'AbortError') return;
            results.innerHTML = '';
            console.warn('[ROWMAN] predictive search unavailable', err);
          });
      }, 260);

      toggles.forEach(function (t) { t.addEventListener('click', open); });
      if (closeBtn) closeBtn.addEventListener('click', close);
      if (input) input.addEventListener('input', run);
      document.addEventListener('keydown', onKey);
      if (form) {
        form.addEventListener('submit', function (e) {
          if (!input || !input.value.trim()) e.preventDefault();
        });
      }

      return function () {
        toggles.forEach(function (t) { t.removeEventListener('click', open); });
        if (closeBtn) closeBtn.removeEventListener('click', close);
        if (input) input.removeEventListener('input', run);
        document.removeEventListener('keydown', onKey);
        document.body.classList.remove('search-open');
      };
    }
  });

  /* ═══════════════════════ MODULE: email capture popup ═══════════════════════
     Throttled exactly as the prototype was: dismissed visitors wait N days,
     signed-up visitors never see it again. */

  register({
    name: 'EmailPopup',
    scope: '[data-email-popup]',
    mount: function (popup) {
      var KEY = 'rowman.popup.v2';
      var delay = parseInt(popup.getAttribute('data-delay') || '5000', 10);
      var throttleDays = parseInt(popup.getAttribute('data-throttle') || '7', 10);
      var forcePreview = popup.getAttribute('data-force') === 'true';
      var closeBtn = popup.querySelector('[data-popup-close]');
      var overlay = popup.querySelector('[data-popup-overlay]');
      var form = popup.querySelector('form');
      var releaseTrap = null;
      var timer = null;

      var state = safeLocal(KEY, { dismissedAt: 0, signedUp: false });

      function eligible() {
        if (forcePreview) return true;
        if (state.signedUp) return false;
        if (!state.dismissedAt) return true;
        return (Date.now() - state.dismissedAt) > throttleDays * 86400000;
      }

      function open() {
        popup.classList.add('is-open');
        popup.setAttribute('aria-hidden', 'false');
        document.body.classList.add('modal-open');
        releaseTrap = trapFocus(popup);
        var first = popup.querySelector(FOCUSABLE);
        if (first) first.focus();
      }

      function close(signedUp) {
        popup.classList.remove('is-open');
        popup.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('modal-open');
        if (releaseTrap) { releaseTrap(); releaseTrap = null; }
        state.dismissedAt = Date.now();
        if (signedUp) state.signedUp = true;
        setLocal(KEY, state);
      }

      function onKey(e) {
        if (e.key === 'Escape' && popup.classList.contains('is-open')) close(false);
      }

      if (closeBtn) closeBtn.addEventListener('click', function () { close(false); });
      if (overlay) overlay.addEventListener('click', function () { close(false); });
      document.addEventListener('keydown', onKey);
      if (form) {
        form.addEventListener('submit', function () {
          state.signedUp = true;
          setLocal(KEY, state);
        });
      }
      // Shopify redirects back with ?customer_posted=true after a successful signup
      if (/customer_posted=true/.test(window.location.search)) {
        state.signedUp = true;
        setLocal(KEY, state);
      }

      if (eligible() && !window.Shopify?.designMode) {
        timer = setTimeout(open, delay);
      } else if (forcePreview) {
        timer = setTimeout(open, 400);
      }

      return function () {
        clearTimeout(timer);
        document.removeEventListener('keydown', onKey);
        document.body.classList.remove('modal-open');
      };
    }
  });

  /* ═══════════════════════ MODULE: accordion (product details, FAQ) ═══════════════════════ */

  register({
    name: 'Accordion',
    scope: '[data-accordion]',
    mount: function (root) {
      var heads = root.querySelectorAll('[data-accordion-trigger]');
      function onClick(e) {
        var trigger = e.currentTarget;
        var panel = document.getElementById(trigger.getAttribute('aria-controls'));
        var open = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', open ? 'false' : 'true');
        if (panel) panel.hidden = open;
        trigger.closest('.pd-item, .accordion-item')?.classList.toggle('is-open', !open);
      }
      heads.forEach(function (h) { h.addEventListener('click', onClick); });
      return function () {
        heads.forEach(function (h) { h.removeEventListener('click', onClick); });
      };
    }
  });

  /* ═══════════════════════ MODULE: locked-store password modal ═══════════════════════ */

  register({
    name: 'PasswordModal',
    scope: '[data-password-modal]',
    mount: function (modal) {
      var openers = document.querySelectorAll('[data-password-open]');
      var closeBtn = modal.querySelector('[data-password-close]');
      var releaseTrap = null;

      function open(e) {
        if (e) e.preventDefault();
        modal.classList.add('is-open');
        modal.setAttribute('aria-hidden', 'false');
        document.body.classList.add('modal-open');
        releaseTrap = trapFocus(modal);
        var f = modal.querySelector(FOCUSABLE);
        if (f) f.focus();
      }
      function close() {
        modal.classList.remove('is-open');
        modal.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('modal-open');
        if (releaseTrap) { releaseTrap(); releaseTrap = null; }
      }
      function onKey(e) { if (e.key === 'Escape') close(); }

      openers.forEach(function (o) { o.addEventListener('click', open); });
      if (closeBtn) closeBtn.addEventListener('click', close);
      document.addEventListener('keydown', onKey);
      if (modal.querySelector('.errors, .lock-shopify-errors li')) open();

      return function () {
        openers.forEach(function (o) { o.removeEventListener('click', open); });
        document.removeEventListener('keydown', onKey);
        document.body.classList.remove('modal-open');
      };
    }
  });

}());

/* ============================================================================
   ROWMAN — contact mode toggle (fidelity repair)
   Progressive enhancement for the Client Support / Press-Business tabs from
   the original #page-contact. With JS off every field is visible and the form
   still submits, so nothing is ever unreachable.
   ============================================================================ */
(function () {
  'use strict';
  var R = window.ROWMAN;
  if (!R) return;

  R.register({
    name: 'ContactModes',
    scope: '[data-contact-root]',
    mount: function (root) {
      var toggle = root.querySelector('[data-contact-toggle]');
      if (!toggle) return;

      var tabs = Array.prototype.slice.call(toggle.querySelectorAll('[data-mode]'));
      var select = root.querySelector('[data-contact-subject]');
      var modeField = root.querySelector('[data-contact-mode-field]');
      var allOptions = select
        ? Array.prototype.slice.call(select.querySelectorAll('option'))
        : [];

      function apply(mode) {
        tabs.forEach(function (t) {
          var on = t.getAttribute('data-mode') === mode;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          if (on && modeField) modeField.value = t.textContent.trim().replace(/^\d+\s*/, '');
        });

        root.querySelectorAll('[data-show-for]').forEach(function (el) {
          var show = el.getAttribute('data-show-for') === mode;
          el.hidden = !show;
          el.querySelectorAll('input, select, textarea').forEach(function (field) {
            // Hidden fields must not block validation or post stale values
            field.disabled = !show;
          });
        });

        if (select) {
          var first = null;
          allOptions.forEach(function (opt) {
            var match = !opt.getAttribute('data-mode') || opt.getAttribute('data-mode') === mode;
            opt.hidden = !match;
            opt.disabled = !match;
            if (match && !first) first = opt;
          });
          if (first && (select.selectedOptions[0] || {}).hidden !== false) select.value = first.value;
        }
      }

      function onClick(e) {
        var tab = e.target.closest('[data-mode]');
        if (!tab || !toggle.contains(tab)) return;
        e.preventDefault();
        apply(tab.getAttribute('data-mode'));
      }

      function onKey(e) {
        if (['ArrowLeft', 'ArrowRight'].indexOf(e.key) === -1) return;
        var i = tabs.indexOf(document.activeElement);
        if (i === -1) return;
        e.preventDefault();
        var next = tabs[(i + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length];
        next.focus();
        next.click();
      }

      toggle.addEventListener('click', onClick);
      toggle.addEventListener('keydown', onKey);
      apply('client');

      return function () {
        toggle.removeEventListener('click', onClick);
        toggle.removeEventListener('keydown', onKey);
      };
    }
  });
}());
